import { ModalInput } from "components/atoms/input/modal/ModalInput";
import Input from "components/atoms/input/Input";
import { Row } from "styles/ModalCommonStyled";
import styled from "styled-components";
import TextArea from "components/atoms/input/TextArea";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import { LEVEL_TYPE } from "api/interfaces/commonInterface.interface";
import {
  EuRequestType,
  GetSubscriptionInstallerResponse,
  InstallerList,
  SUBSCRIPTION_TYPE,
} from "api/interfaces/subscription.interface";
import { formatUppercaseOnly } from "utils/functions";
import { useAppSelector } from "redux/hooks";
import { useMemo } from "react";
import {
  changeServiceNameUnit,
  useKeyType,
} from "hook/subscription/useKeyType";
import SelectComponent from "components/atoms/select/SelectComponent";
import SubscriptionOrgLabel from "components/atoms/text/labels/SubscriptionOrgLabel";
import { useQuery } from "react-query";
import { getSubscriptionInstallerList } from "api/subscriptionAPI";
import { Option } from "utils/options";

type Props = {
  keyInfo?: EuRequestType;
  onChangeKeyData: (name: string, value: any) => void;
  validateMsg?: Record<string, string>;
};

export default function RequestKeyInfo(props: Props): JSX.Element {
  const {
    accountId,
    accountName,
    accountNumber,
    accountLevel,
  }: ProfileAccountInfo = useAppSelector((state) => state.accountSettings);
  const { keyInfo, onChangeKeyData } = props;
  const { durationMonthOptions } = useKeyType();

  const accountInfo = useMemo(
    () => {
      // if (requestInfo && requestInfo.accountName && requestInfo.accountNumber) {
      //   return {
      //     accountName: requestInfo.accountName,
      //     accountNumber: requestInfo.accountNumber,
      //   };
      // }
      return {
        accountName,
        accountNumber,
      };
    },
    [
      // requestInfo
    ]
  );

  const info: EuRequestType | null = useMemo(() => {
    // if (requestInfo) {
    //   return requestInfo as EuRequestType;
    // }

    if (keyInfo) {
      return keyInfo;
    }
    return null;
  }, [
    // requestInfo,
    keyInfo,
  ]);

  const { data: installerData, isFetching: installerIsFetching } = useQuery(
    ["getSubscriptionInstallerList", accountId],
    () =>
      getSubscriptionInstallerList({
        accountId,
      }),
    {
      retry: 0,
      select: (res: GetSubscriptionInstallerResponse) => res?.result ?? null,
      refetchOnWindowFocus: false,
    }
  );

  const installerList: Option[] = useMemo(() => {
    return (installerData || []).map((install: InstallerList) => {
      return {
        label: install.name || install.email,
        value: install.email,
      };
    });
  }, [installerData]);

  return (
    <Wrapper>
      <SubscriptionOrgLabel
        accountName={accountInfo.accountName}
        accountNumber={accountInfo.accountNumber}
        accountLevel={accountLevel}
      />

      {info !== null && (
        <>
          <Row>
            <ModalInput label="Requester" essential>
              <Input disabled value={info.requester} />
            </ModalInput>
            <ModalInput label="Recipient" essential>
              <SelectComponent
                placeholder="Select Recipient"
                options={installerList}
                loading={installerIsFetching}
                value={info.installer}
                onChange={(value) => {
                  onChangeKeyData("installer", value);
                }}
                data-testid="subscription-modal-request-key-selects-change-key-data"
              />
            </ModalInput>
          </Row>
          <ModalInput label="Key Type" essential>
            <Input disabled value={formatUppercaseOnly(info.keyType)} />
          </ModalInput>
          <Row>
            <ModalInput
              label="Duration"
              essential
              isValidated={props.validateMsg?.duration !== ""}
              validatedWord={props.validateMsg?.duration}
            >
              <SelectComponent
                placeholder="Select duration"
                options={durationMonthOptions(
                  info.keyType as SUBSCRIPTION_TYPE,
                  accountLevel === LEVEL_TYPE.DW
                )}
                value={info.duration && String(info.duration)}
                onChange={(value: string) =>
                  onChangeKeyData("duration", Number(value))
                }
                data-testid="subscription-modal-request-key-selects-change-key-data-2"
              />
            </ModalInput>
            <ModalInput
              label={changeServiceNameUnit(info.keyType)}
              essential
              isValidated={props.validateMsg?.number !== ""}
              validatedWord={props.validateMsg?.number}
            >
              <Input
                type="text"
                inputMode="numeric"
                pattern="\d*"
                placeholder={
                  info.keyType === SUBSCRIPTION_TYPE.STANDARD
                    ? "Enter number of devices"
                    : "Enter storage amount in TB"
                }
                value={
                  info.keyType === SUBSCRIPTION_TYPE.STANDARD
                    ? info.channels ?? ""
                    : info.capacity ?? ""
                }
                onChange={(e) => {
                  const rawValue = e.target.value;
                  if (/^\d*$/.test(rawValue)) {
                    onChangeKeyData(
                      info.keyType === SUBSCRIPTION_TYPE.STANDARD
                        ? "channels"
                        : "capacity",
                      rawValue === "" ? "" : Number(rawValue)
                    );
                  }
                }}
                data-testid="subscription-modal-request-key-input-test"
              />

              {/* {info.keyType === SUBSCRIPTION_TYPE.STANDARD ? (
                <SelectComponent
                  placeholder="Select number of channels"
                  options={
                    info.duration
                      ? standardChannelOptionsByDuration(info.duration)
                      : []
                  }
                  value={
                    info.duration
                      ? standardChannelOptionsByDuration(info.duration).find(
                          (opt) => opt.value === String(info.channels)
                        )
                      : undefined
                  }
                  onChange={(value: string) =>
                    onChangeKeyData("channels", Number(value))
                  }
                />
              ) : (
               <Input
                 type="text"
                 inputMode="numeric"
                 pattern="\d*"
                 placeholder={"Enter storage amount in TB"}
                 value={info.capacity}
                 onChange={(e) => {
                   const rawValue = e.target.value;
                   if (/^\d*$/.test(rawValue)) {
                     onChangeKeyData(
                       "capacity",
                       rawValue === "" ? "" : Number(rawValue)
                     );
                   }
                 }}
               />
              )} */}
            </ModalInput>
          </Row>

          <div id="request-message-wrapper">
            <ModalInput label="Send a Message to Your Installer (Optional)">
              <TextArea
                placeholder="Please enter a message."
                value={info.message}
                name="message"
                onChange={(e) => onChangeKeyData("message", e.target.value)}
                data-testid="subscription-modal-request-key-message-change-key-data"
              />
            </ModalInput>
          </div>
        </>
      )}
    </Wrapper>
  );
}

const Wrapper = styled.div`
  width: 100%;

  #request-message-wrapper {
    padding-top: 26px;
  }
`;
